import { Link, useParams } from "react-router-dom";
import { MapPin, ShieldCheck } from "lucide-react";
import { api, useApi } from "../api";
import { PageShell } from "../components/Layout";
import { ChapterTag, FaqAccordion, Marquee, SpotlightCard } from "../components/Motion";
import Seo, { breadcrumbSchema, faqSchema } from "../components/Seo";
import { LinkPills, Loading, PageHero, SectionBlock } from "../components/Sections";

export default function Region() {
  const { slug } = useParams();
  const { data, loading, error } = useApi(() => api.region(slug), [slug]);

  if (loading) {
    return (
      <PageShell>
        <Loading />
      </PageShell>
    );
  }
  if (error || !data) {
    return (
      <PageShell>
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4" data-testid="region-not-found">
          <p className="font-display font-black uppercase text-4xl text-white">Region not found</p>
          <Link to="/locations" className="font-mono text-xs uppercase tracking-widest text-amber border border-amber px-5 py-3">
            View all UK locations
          </Link>
        </div>
      </PageShell>
    );
  }

  const origin = window.location.origin;
  const name = data.region.name;

  return (
    <PageShell>
      <Seo title={data.title} description={data.description} path={`/regions/${slug}`} schema={faqSchema(data.faqs)} />
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema(data.breadcrumb, origin))}</script>

      <PageHero eyebrow={data.eyebrow} lines={data.h1_lines} intro={data.intro} breadcrumb={data.breadcrumb} image={data.image} />
      <Marquee items={data.sections[1].services.map((s) => `${s.name} ${name}`)} />

      {data.sections.map((sec, i) => (
        <SectionBlock key={i} index={i} heading={sec.heading} paragraphs={sec.paragraphs} bullets={sec.bullets}>
          {sec.cities && (
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5" data-testid="region-cities-grid">
              {sec.cities.map((c) => (
                <Link
                  key={c.slug}
                  to={`/locations/${c.slug}`}
                  data-testid={`region-city-${c.slug}`}
                  className="group flex items-center gap-2.5 border border-white/10 bg-card px-4 py-3 hover:border-amber transition-colors duration-300"
                >
                  <MapPin className="w-3.5 h-3.5 text-amber shrink-0" />
                  <span className="min-w-0">
                    <span className="block font-display font-bold uppercase tracking-wide text-sm text-white group-hover:text-amber transition-colors truncate">
                      Security {c.name}
                    </span>
                    <span className="block font-mono text-[9px] uppercase tracking-widest text-gray-500 truncate">{c.county}</span>
                  </span>
                </Link>
              ))}
            </div>
          )}
          {sec.services && (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="region-services-grid">
              {sec.services.map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`region-service-${s.slug}`} className="block">
                  <SpotlightCard className="p-4 hover:border-amber/60 transition-colors duration-300 flex items-center gap-3">
                    <ShieldCheck className="w-4 h-4 text-amber shrink-0" />
                    <span className="font-display font-bold uppercase tracking-wide text-base text-white">{s.name}</span>
                  </SpotlightCard>
                </Link>
              ))}
            </div>
          )}
        </SectionBlock>
      ))}

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-14" data-testid="region-faq-section">
        <ChapterTag num="FAQ" title={`${name.toUpperCase()} — QUESTIONS`} />
        <FaqAccordion faqs={data.faqs} testIdPrefix="region-faq" />
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
        <ChapterTag num="GRID" title="NEIGHBOURING REGIONS" />
        <LinkPills items={data.neighbours} prefix="/regions" labelFn={(r) => `Security Services in ${r.name}`} testIdPrefix="region-neighbour" />
      </section>
    </PageShell>
  );
}
